import { useId, type ReactNode } from "react";
import { Check } from "lucide-react";
import { Brand } from "@/shared/components/ui/Brand";
import { Card } from "@/shared/components/ui/Card";
import { SectionHeader } from "@/shared/components/ui/SectionHeader";
import { StatusBadge, type StatusBadgeProps } from "@/shared/components/ui/StatusBadge";
import { cn } from "@/shared/utils/cn";
import type { Subscription, SubscriptionStatus } from "../types/subscriptionTypes";
import { formatPlanDate, formatUsageLabel, isFreePlan } from "../utils/subscriptionFormat";

const statuses = {
  active: { label: "Active", tone: "positive" },
  trialing: { label: "Trial", tone: "positive" },
  incomplete: { label: "Payment pending", tone: "pending" },
  past_due: { label: "Payment issue", tone: "danger" },
  unpaid: { label: "Payment issue", tone: "danger" },
  paused: { label: "Paused", tone: "pending" },
  incomplete_expired: { label: "Ended", tone: "neutral" },
  canceled: { label: "Ended", tone: "neutral" },
} satisfies Record<SubscriptionStatus, { label: string; tone: StatusBadgeProps["tone"] }>;

function describe(subscription: Subscription) {
  const end = subscription.currentPeriodEnd ? formatPlanDate(subscription.currentPeriodEnd, true) : undefined;
  switch (subscription.status) {
    case null:
    case "canceled":
    case "incomplete_expired":
      return "Booking essentials with unlimited email confirmations and reminders. Upgrade to text customers or use AI.";
    case "incomplete":
      return "Stripe is confirming your payment. Your features turn on as soon as it completes.";
    case "past_due":
    case "unpaid":
      return "Stripe couldn't collect your last payment. Update your payment method to keep your features.";
    case "paused":
      return "Your plan is paused. Resume it in Stripe to use its features again.";
    default:
      if (subscription.cancelAtPeriodEnd) return end ? `Your plan ends on ${end}.` : "Your plan ends at the end of this billing period.";
      return end ? `Renews on ${end}.` : "Your plan renews monthly.";
  }
}

interface CurrentPlanCardProps {
  subscription: Subscription;
  action?: ReactNode;
}

export function CurrentPlanCard({ subscription, action }: CurrentPlanCardProps) {
  const headingId = useId();
  const free = isFreePlan(subscription);
  const status = subscription.status && !free ? statuses[subscription.status] : { label: "Free", tone: "neutral" as const };
  const positive = status.tone === "positive";
  const usage = subscription.usage.filter((entry) => entry.limit > 0 || entry.used > 0);

  return (
    <Card tone={positive ? "matcha" : "default"} role="region" aria-labelledby={headingId}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Brand wordmark />
        <StatusBadge tone={status.tone} className={cn("gap-1", positive && "bg-card/80")}>
          {positive && <Check className="size-3.5" aria-hidden="true" />}
          {status.label}
        </StatusBadge>
      </div>
      <div className="flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-3 sm:max-w-[480px]">
          <SectionHeader title={free ? "Free plan" : subscription.name ?? "Your plan"} headingId={headingId} />
          <p className="text-sm leading-relaxed">{describe(subscription)}</p>
        </div>
        {action && <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:flex-wrap">{action}</div>}
      </div>
      {usage.length > 0 && (
        <div className={cn("mt-6 space-y-1 border-t pt-4 text-xs", positive ? "border-primary/40" : "border-border")}>
          {usage.map((entry) => (
            <p key={entry.metric}>{formatUsageLabel(entry)}</p>
          ))}
        </div>
      )}
    </Card>
  );
}
